'use client';

import { createDiagnosedResizeObserver } from '@/lib/resize-observer-diagnostics';
import { createAnimationFrameTask } from '@/lib/element-resize';

import { forwardRef, useCallback, useEffect, useId, useRef, useState, type ReactNode, type ButtonHTMLAttributes } from 'react';
import styles from './LiquidGlass.module.css';

/** A rounded lens map: neutral center, inward refraction at the curved rim. */
function createLensMap(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return '';
  const image = context.createImageData(width, height);
  const radius = Math.min(height / 2, width / 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const cx = Math.max(radius, Math.min(width - radius, x));
      const cy = Math.max(radius, Math.min(height - radius, y));
      let nx = x - cx;
      let ny = y - cy;
      const length = Math.hypot(nx, ny);
      const distance = radius - length;
      const rim = Math.max(0, 1 - Math.max(0, distance) / 12);
      const bend = distance >= 0 ? Math.sin(rim * Math.PI * .82) * .9 : 0;
      nx = length ? nx / length : 0;
      ny = length ? ny / length : 0;
      const offset = (y * width + x) * 4;
      image.data[offset] = Math.round(128 - nx * bend * 127);
      image.data[offset + 1] = Math.round(128 - ny * bend * 127);
      image.data[offset + 2] = 128;
      image.data[offset + 3] = 255;
    }
  }
  context.putImageData(image, 0, 0);
  return canvas.toDataURL();
}

type LiquidGlassProps = { children: ReactNode; className?: string } & (
  | { as?: 'div' | 'span' }
  | ({ as: 'button' } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'>)
);

export const LiquidGlass = forwardRef<HTMLDivElement | HTMLButtonElement | HTMLSpanElement, LiquidGlassProps>(function LiquidGlass(props, forwardedRef) {
  const { children, className = '', as = 'div', ...buttonProps } = props;
  const id = `glass-${useId().replace(/:/g, '')}`;
  const root = useRef<HTMLDivElement | HTMLButtonElement | HTMLSpanElement | null>(null);
  const [lens, setLens] = useState({ url: '', width: 1, height: 1 });
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let lastWidth = 0;
    let lastHeight = 0;
    const frame = createAnimationFrameTask(() => {
      // Lens pixels follow layout size, not hover/entry transforms.
      const width = Math.max(1, element.offsetWidth);
      const height = Math.max(1, element.offsetHeight);
      if (width === lastWidth && height === lastHeight) return;
      lastWidth = width;
      lastHeight = height;
      setLens({ url: createLensMap(width, height), width, height });
    });
    const observer = createDiagnosedResizeObserver('LiquidGlass', frame.schedule);
    observer.observe(element);
    return () => { observer.disconnect(); frame.dispose(); };
  }, [as]);
  const content = <>
    <svg className={styles.definitions} aria-hidden="true" focusable="false">
      <defs>
        <filter id={id} x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
          <feImage href={lens.url || undefined} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="lens" />
          <feDisplacementMap in="SourceGraphic" in2="lens" scale="22" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
    </svg>
    <span className={styles.transmission} aria-hidden="true" style={lens.url ? { backdropFilter: `url(#${id}) blur(2px) saturate(1.16)` } : undefined} />
    {children}
  </>;
  const ref = useCallback((element: HTMLDivElement | HTMLButtonElement | HTMLSpanElement | null) => {
    root.current = element;
    if (typeof forwardedRef === 'function') forwardedRef(element);
    else if (forwardedRef) forwardedRef.current = element;
  }, [forwardedRef]);
  if (as === 'button') {
    return <button {...buttonProps} ref={ref} className={`${styles.glass} ${className}`} data-liquid-glass>{content}</button>;
  }
  if (as === 'span') {
    return <span ref={ref} className={`${styles.glass} ${className}`} data-liquid-glass>{content}</span>;
  }
  return <div ref={ref} className={`${styles.glass} ${className}`} data-liquid-glass>{content}</div>;
});
